import { PageHeader } from '@ant-design/pro-components';
import { Content } from 'antd/es/layout/layout';
import React from 'react';
import DefaultLayout from '@src/components/DefaultLayout';

import {
    Table,
    Tag,
    Divider,
    TextField,
    Button,
} from '@workspace/design-system/src/components';
import { Pagination } from '@src/components/Pagination';
import { CustomSelect } from '@src/components/CustomSelect';
import { useEnterpriseInquiryController } from '@src/hooks/enterpriseInquiry/use-enterprise-inquiry-controller';
import type { PlanInquiryResponseModel, PlanInquiryStatus } from '@src/models/enterprise-inquiry';
import {
    INQUIRY_STATUSES_FILTER,
    INQUIRY_MANAGERS_FILTER,
    getStatusColor,
    getStatusLabel,
    getManagerLabel,
} from '@src/constants/enterprise-inquiry';

export default function StudioEnterpriseInquiry() {
    const {
        inquiries,
        isLoading,
        isFetching,
        searchInput,
        statusFilter,
        managerFilter,
        setSearchInput,
        setStatusFilter,
        setManagerFilter,
        currentPage,
        totalPages,
        goToPage,
        handleSearch,
        openDetailModal,
    } = useEnterpriseInquiryController();

    // 필터 변경 핸들러
    const handleStatusFilterChange = (value: string | undefined) => {
        setStatusFilter((value || 'all') as typeof statusFilter);
    };

    const handleManagerFilterChange = (value: string | undefined) => {
        setManagerFilter((value || 'all') as typeof managerFilter);
    };

    // 테이블 컬럼 정의
    const columns: any[] = [
        {
            id: 'organizationName',
            key: 'organizationName',
            header: '기업 / 단체명',
            accessorKey: 'organizationName',
        },
        {
            id: 'contactName',
            key: 'contactName',
            header: '담당자',
            accessorKey: 'contactName',
        },
        {
            id: 'jobTitle',
            key: 'jobTitle',
            header: '부서 및 직책',
            accessorKey: 'jobTitle',
            cell: ({ row }: { row: { original: PlanInquiryResponseModel } }) => (
                <div>
                    {row.original.jobTitle}
                </div>
            ),
        },
        {
            id: 'phone',
            key: 'phone',
            header: '연락처',
            accessorKey: 'phone',
        },
        {
            id: 'email',
            key: 'email',
            header: '이메일',
            accessorKey: 'email',
        },
        {
            id: 'status',
            key: 'status',
            header: '문의 상태',
            accessorKey: 'status',
            cell: ({ getValue }: { getValue: () => PlanInquiryStatus }) => {
                const status = getValue();
                return (
                    <Tag
                        color={getStatusColor(status)}
                        size="S-16"
                        text={getStatusLabel(status)}
                    />
                );
            },
        },
        {
            id: 'adminId',
            key: 'adminId',
            header: '문의 담당자',
            accessorKey: 'adminId',
            cell: ({ getValue }: { getValue: () => string | null | undefined }) => {
                const adminId = getValue();
                return (
                    <Tag
                        color="gray"
                        size="S-16"
                        text={getManagerLabel(adminId)}
                    />
                );
            },
        },
    ];

    const isSearching = isLoading || isFetching;

    // 엔터 키 핸들러 (TextField용)
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <DefaultLayout>
            <PageHeader title="엔터프라이즈 도입 문의" style={{ margin: '16px 20px 0px 20px' }} />
            <Divider direction="horizontal" />
            <Content style={{ padding: '0 24px', margin: '0px 20px' }}>
                <div className="px-4 py-6 sm:px-0">
                    <div className="bg-white rounded-[24px] p-10 shadow-sm">
                        {/* 검색 및 필터 */}
                        <div className="flex items-center justify-between mb-6 gap-4">
                            <div className="flex items-center gap-2 flex-1 max-w-[400px]">
                                <div className="flex-1">
                                    <TextField
                                        placeholder="기업 / 단체명, 담당자명, 연락처, 이메일로 검색 가능"
                                        value={searchInput}
                                        onChange={setSearchInput}
                                        onKeyDown={handleKeyDown}
                                    />
                                </div>
                                <Button styleType="text-primary" styleSize="S-32" onClick={handleSearch}>
                                    검색
                                </Button>
                            </div>

                            <div className="flex gap-4">
                                <CustomSelect
                                    placeholder="문의 상태"
                                    items={INQUIRY_STATUSES_FILTER}
                                    value={statusFilter === 'all' ? undefined : statusFilter}
                                    onSelect={handleStatusFilterChange}
                                />

                                <CustomSelect
                                    placeholder="문의 담당자"
                                    items={INQUIRY_MANAGERS_FILTER}
                                    value={managerFilter === 'all' ? undefined : managerFilter}
                                    onSelect={handleManagerFilterChange}
                                />
                            </div>
                        </div>

                        {/* 테이블 영역 */}
                        <div className="bg-white overflow-hidden">
                            {isSearching ? (
                                <div className="flex items-center justify-center py-20">
                                    <div className="flex flex-col items-center gap-3">
                                        <div className="w-10 h-10 border-4 border-[#F0F0F0] border-t-[#3C78F4] rounded-full animate-spin" />
                                        <p className="text-[14px] text-[#595959]">로딩 중...</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <Table
                                        data={inquiries}
                                        columns={columns}
                                        onRowClick={(row) => openDetailModal(row.original)}
                                        getRowId={(row) => row.id}
                                        emptyContent={
                                            <div className="text-center py-8 text-gray-500">
                                                검색 결과가 없습니다.
                                            </div>
                                        }
                                    />

                                    {/* 커스텀 페이지네이션 */}
                                    {inquiries.length > 0 && totalPages > 0 && (
                                        <div className="mt-6">
                                            <Pagination
                                                currentPage={currentPage}
                                                totalPages={totalPages}
                                                onPageChange={goToPage}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Content>
        </DefaultLayout>
    );
}
